import React, { useState, useEffect } from 'react';
import { Grid, Button, TextField, FormControl, InputLabel, Select, MenuItem,Box,Autocomplete } from '@mui/material';
import dayjs from 'dayjs';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { DataGrid } from '@mui/x-data-grid';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import { apiUrl } from '../../apiUrl/ApiUrl';

export default function BuyerInStock({ data }) {
  const [needStartDate, setNeedStartDate] = useState(null);
  const [needEndDate, setNeedEndDate] = useState(null);
  const [piNum, setPiNum] = useState('');
  const [drawNum, setDrawNum] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [status, setStatus] = useState('未入庫'); // 狀態預設未入庫
  const [purchaseRows, setPurchaseRows] = useState(data?.buyerDtos || []);
  const [loading, setLoading] = useState(false);
  const [manufacturerOptions, setManufacturerOptions] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);

  // DataGrid 欄位結構
  const columns = [
    { field: 'date', headerName: '採購日期', width: 100, valueFormatter: params => params.value ? dayjs(params.value).format('YYYY/MM/DD') : '' },
    { field: 'manufacturer', headerName: '廠商', width: 90 },
    { field: 'needDate', headerName: '需求日', width: 100, valueFormatter: params => params.value ? dayjs(params.value).format('YYYY/MM/DD') : '' },
    { field: 'drawNum', headerName: '圖號', width: 110 },
    { field: 'itemName', headerName: '物品名', width: 120 },
    { field: 'unit', headerName: '單位', width: 60 },
    { field: 'amount', headerName: '數量', width: 80, valueFormatter: ({ value }) => Number(value).toFixed(2) },
    { field: 'unitPrice', headerName: '單價', width: 80, valueFormatter: ({ value }) => Number(value).toFixed(2) },
    { field: 'moneyAmount', headerName: '金額', width: 80, valueFormatter: ({ value }) => Number(value).toFixed(2) },
    { field: 'orderNum', headerName: '單號', width: 110 },
    { field: 'piNum', headerName: '批號', width: 110 },
    { field: 'approve', headerName: '核准', width: 120 },
    { field: 'id', headerName: 'ID', width: 90 }
  ];

  // 查詢功能
  const fetchMain = async () => {
    setLoading(true);
    try {
      const params = {
        needStartDate: needStartDate ? dayjs(needStartDate).format('YYYY-MM-DD') : '',
        needEndDate: needEndDate ? dayjs(needEndDate).format('YYYY-MM-DD') : '',
        piNum,
        drawNum,
        manufacturer: selectedManufacturer?.manufacturer_AbbreviationName || '',
        status,
        page: 1,
      };
      const res = await axios.get(`${apiUrl}/Buyer/GetMainsWithInStock`, { params });
      setPurchaseRows(res.data.buyerDtos ?? []);
    } finally {
      setLoading(false);
    }
  };

  // 匯出 Excel
  const exportExcel = async () => {
    setLoading(true);
    try {
      const params = {
        needStartDate: needStartDate ? dayjs(needStartDate).format('YYYY-MM-DD') : '',
        needEndDate: needEndDate ? dayjs(needEndDate).format('YYYY-MM-DD') : '',
        piNum,
        drawNum,
        manufacturer: selectedManufacturer?.manufacturer_AbbreviationName || '',
        status,
      };
      const response = await axios.get(`${apiUrl}/Buyer/DataToExcel`, {
        responseType: 'blob',
        params
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', '請採購資料.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      alert('匯出失敗');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const fetchManufacturers = async () => {
      try {
        const res = await axios.get(`${apiUrl}/Manufacturer/list`);
        setManufacturerOptions(res.data.manufacturers || []);
      } catch (e) {
        setManufacturerOptions([]);
      }
    };
    fetchManufacturers();
  }, []);
  // 搜尋清除
  const handleClear = () => {
    setNeedStartDate(null);
    setNeedEndDate(null);
    setPiNum('');
    setDrawNum('');
    setManufacturer('');
    setStatus('未入庫');
    setPurchaseRows([]);
  };

  return (
    <div className='container-fluid'>
      {/* 遮罩 */}
      <Backdrop sx={{ color: '#fff', zIndex: theme => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {/* 查詢區 */}
      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={2}>
          <DatePicker
            selected={needStartDate}
            onChange={date => setNeedStartDate(date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="需求日起"
            customInput={<TextField label="需求日起" fullWidth size="small" />}
            isClearable
          />
        </Grid>
        <Grid item xs={2}>
          <DatePicker
            selected={needEndDate}
            onChange={date => setNeedEndDate(date)}
            dateFormat="yyyy-MM-dd"
            placeholderText="需求日迄"
            customInput={<TextField label="需求日迄" fullWidth size="small" />}
            isClearable
          />
        </Grid>
        <Grid item xs={2}>
          <TextField label="批號" value={piNum} onChange={e => setPiNum(e.target.value)} fullWidth size="small" />
        </Grid>
        <Grid item xs={2}>
          <TextField label="圖號" value={drawNum} onChange={e => setDrawNum(e.target.value)} fullWidth size="small" />
        </Grid>
        <Grid item xs={2}>
            <Autocomplete
                options={manufacturerOptions}
                getOptionLabel={option => option.manufacturer_Seq + option.manufacturer_AbbreviationName}
                isOptionEqualToValue={(option, value) =>
                option.manufacturer_AbbreviationName === value?.manufacturer_AbbreviationName
                }
                value={selectedManufacturer}
                onChange={(e, newValue) => setSelectedManufacturer(newValue)}
                renderInput={params => (
                <TextField {...params} label="廠商" fullWidth size="small" />
                )}
                clearOnEscape
            />
        </Grid>
        
        <Grid item xs={1}>
          <Button variant="contained" onClick={fetchMain} fullWidth>查詢</Button>
        </Grid>
        <Grid item xs={1}>
          <Button variant="outlined" onClick={handleClear} fullWidth>清除</Button>
        </Grid>
      </Grid>
      
      {/* 匯出Excel與狀態選擇，橫向排列 */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        
        <FormControl size="small">
          <InputLabel id="status-label">狀況</InputLabel>
          <Select
            labelId="status-label"
            value={status}
            label="狀態"
            onChange={e => setStatus(e.target.value)}
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="未入庫">未入庫</MenuItem>
            <MenuItem value="入庫">入庫</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" onClick={exportExcel} sx={{ mr: 2 }}>匯出 Excel</Button>
      </Box>
      {/* 資料表 */}
      <div style={{ height: '70vh', width: '100%', marginBottom: 16 }}>
        <DataGrid
          rows={purchaseRows}
          columns={columns}
          loading={loading}
          pageSize={100}
          rowsPerPageOptions={[100]}
          pagination
          disableRowSelectionOnClick
          getRowId={row => row.id}
          rowHeight={30}
          headerHeight={32}
          sx={{ backgroundColor: '#fff' }}
          columnVisibilityModel={{
            id: false, // <-- 這裡明確關掉 id 欄位
          }}
        />
      </div>
    </div>
  );
}
